import type { CSSProperties, FC, HTMLAttributes } from 'react'
import { useEffect, useRef, useState } from 'react'
import { PulseProgressBox } from '../progress/index.js'

interface ImageProps extends HTMLAttributes<HTMLDivElement> {
  url?: string
  className?: string
  rotation?: number
}

export const Image: FC<ImageProps> = props => {
  const { url, className, rotation = 0, ...divProps } = props

  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  // When the URL changes: reset the loader, then handle the cached-image race
  // where the browser sets <img>.complete = true (and naturalWidth > 0) before
  // React attaches the onLoad listener, which would otherwise leave the loader
  // up forever because onLoad never fires.
  useEffect(() => {
    if (!url) {
      setLoaded(false)
      return
    }
    const img = imgRef.current
    setLoaded(Boolean(img?.complete && img.naturalWidth > 0))
  }, [url])

  const handleLoad = () => {
    setLoaded(true)
  }

  const style: CSSProperties = className
    ? {
        transform: `rotate(${rotation}deg)`,
      }
    : {
        transform: `rotate(${rotation}deg)`,
        width: '100%',
        objectFit: 'cover',
      }

  return (
    <div {...divProps}>
      {url && (
        <img
          key={url}
          ref={imgRef}
          className={className}
          onLoad={handleLoad}
          style={style}
          src={url}
          contentEditable={false}
        />
      )}

      {!loaded ? <PulseProgressBox title="Loading image - please wait..." /> : null}
    </div>
  )
}
